/**
 * Tiny fetch wrapper around the public POST /api/requests endpoint.
 *
 * Kept local to the contact module so the rest of the app doesn't get a
 * dependency on a new client library just for this form flow.
 */

// API calls use relative `/api/...` paths so the browser hits whatever
// origin it's currently on. See ContentProvider.jsx for the rationale.

/**
 * @typedef {Object} SubmitResult
 * @property {true}  ok
 * @property {string} id
 */

/**
 * Submit a new internal request.
 * Resolves to `{ ok:true, id }` on success.
 * On validation errors rejects with `{ status:422, fieldErrors:{...} }`.
 * On rate-limit rejects with `{ status:429, message:"..." }`.
 * On other failures rejects with `{ status, message }`.
 */
export async function submitRequest({
  type,
  language,
  preferredReply,
  fields,
  honeypot = "",
  source = "",
}) {
  const res = await fetch(`/api/requests`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      type,
      language,
      preferred_reply: preferredReply,
      source,
      fields,
      // On-wire name intentionally matches the backend's `_HONEYPOT_FIELD_NAME`.
      hp_website: honeypot,
    }),
  });

  let data = null;
  try { data = await res.json(); } catch { /* non-JSON, ignore */ }

  if (res.ok) return data || { ok: true };

  if (res.status === 422) {
    const fieldErrors =
      (data && data.detail && data.detail.field_errors) || {};
    const err = new Error("Validation error");
    err.status = 422;
    err.fieldErrors = fieldErrors;
    throw err;
  }

  const err = new Error(
    (data && (data.detail || data.message)) || `HTTP ${res.status}`
  );
  err.status = res.status;
  throw err;
}
